import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FolderKanban, Ticket, AlarmClock, AlertTriangle, CheckCircle2, ListTodo, CalendarDays, Image as ImageIcon, FileStack, ClipboardCheck, CalendarClock,
} from "lucide-react";
import api from "@/lib/api";
import { STATUS, labelStatus } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import AuthImage from "@/components/AuthImage";
import Lightbox from "@/components/Lightbox";
import PageHelp from "@/components/PageHelp";
import DashboardTasksCard from "@/components/tasks/DashboardTasksCard";

function Kpi({ icon: Icon, label, value, color, testid, sub, to }) {
  const card = (
    <Card className={`p-4 relative overflow-hidden fade-up ${to ? "cursor-pointer transition-shadow hover:shadow-md hover:border-primary/40" : ""}`} data-testid={testid}>
      <div className="kpi-accent-bar absolute top-0 left-0 right-0" style={{ background: color }} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-3xl font-head font-extrabold text-slate-900 font-mono-tech">{value}</div>
          <div className="text-sm text-slate-500 mt-1">{label}</div>
          {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
        </div>
        <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
      </div>
    </Card>
  );
  return to ? <Link to={to} className="block" data-testid={`${testid}-link`}>{card}</Link> : card;
}

function ProgressRow({ label, value = 0, done = 0, total = 0, testid, color = "" }) {
  return (
    <div className="flex items-center gap-3 py-0.5" data-testid={testid}>
      <div className="text-xs text-slate-600 w-36 shrink-0">{label}</div>
      <div className="flex-1 sm:w-28 sm:flex-none"><Progress value={value} className={`h-2 ${color}`} /></div>
      <div className="text-sm font-bold font-mono-tech text-primary w-11 text-right" data-testid={`${testid}-value`}>{value}%</div>
      <div className="text-[11px] text-slate-400 w-14">{done}/{total}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [insp, setInsp] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/inspections/dashboard").then((r) => setInsp(r.data)).catch(() => setInsp(null));
  }, []);

  const [lightbox, setLightbox] = useState(null);

  if (!data)
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
    );

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl sm:text-4xl font-head font-extrabold tracking-tight text-slate-900">Dashboard</h1>
            <PageHelp id="dashboard" title="Dashboard" text="Ihre wichtigsten Kennzahlen auf einen Blick: aktive Projekte, offene Tickets, Mängel und Fristen. Ein Klick auf eine Kachel führt direkt in den jeweiligen Bereich." />
          </div>
          <p className="text-slate-500 mt-1">Übersicht über alle Einrichtungen und offenen Vorgänge.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm w-full sm:w-auto" data-testid="overall-progress-tile">
          <div className="text-xs text-slate-500 mb-1.5">Gesamtfortschritt</div>
          <ProgressRow label="Tickets abgearbeitet" value={data.progress} done={data.tickets_done} total={data.tickets_total} testid="progress-tickets" />
          <ProgressRow label="Mängel abgearbeitet" value={data.defect_progress} done={data.resolved_defects} total={data.defects_total} testid="progress-defects" color="[&>div]:bg-green-600" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={FolderKanban} label="Aktive Projekte" value={data.active_projects} color="#D97706" testid="kpi-active-projects" sub={`${data.total_projects} gesamt`} to="/projects" />
        <Kpi icon={Ticket} label="Offene Tickets" value={data.open_tickets} color="#0284C7" testid="kpi-open-tickets" to="/overview/tickets" />
        <Kpi icon={AlarmClock} label="Überfällige Tickets" value={data.overdue_tickets} color="#DC2626" testid="kpi-overdue-tickets" to="/overview/tickets" />
        <Kpi icon={AlertTriangle} label="Offene Mängel" value={data.open_defects} color="#EF4444" testid="kpi-open-defects" to="/overview/defects" />
        <Kpi icon={CheckCircle2} label="Erledigte Mängel" value={data.resolved_defects} color="#16A34A" testid="kpi-resolved-defects" to="/overview/defects" />
        <Kpi icon={ListTodo} label="Aktuelle Aufgaben" value={data.tasks} color="#7C3AED" testid="kpi-tasks" to="/overview/tickets" />
        <Kpi icon={CalendarDays} label="Heutige Termine" value={data.today_deadlines.length} color="#2563EB" testid="kpi-today-deadlines" />
        <Kpi icon={FileStack} label="Baupläne" value={data.total_plans} color="#059669" testid="kpi-plans" to="/overview/plans" />
      </div>

      <DashboardTasksCard />

      {/* Prüfungen & Wartungen (Altenheim) */}
      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/pruefungen?tab=calendar" className="block" data-testid="tile-recurring-link">
          <Card className="p-5 relative overflow-hidden fade-up cursor-pointer transition-shadow hover:shadow-md hover:border-primary/40" data-testid="tile-recurring">
            <div className="kpi-accent-bar absolute top-0 left-0 right-0" style={{ background: "#2563EB" }} />
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 text-slate-500 text-sm font-semibold"><ClipboardCheck className="h-4 w-4 text-blue-600" />Wiederkehrende Prüfungen</div>
                <div className="text-4xl font-head font-extrabold text-slate-900 font-mono-tech mt-2">{insp?.active_count ?? 0}</div>
                <div className="text-xs text-slate-500">aktive Prüfungen & Wartungen</div>
                <div className="flex gap-4 mt-3 text-sm">
                  <span className="text-orange-600 font-semibold">{insp?.due_30 ?? 0}<span className="text-slate-400 font-normal"> · 30 Tage</span></span>
                  <span className="text-red-600 font-semibold">{insp?.due_7 ?? 0}<span className="text-slate-400 font-normal"> · 7 Tage</span></span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400 flex items-center gap-1 justify-end"><CalendarClock className="h-3.5 w-3.5" />Nächste</div>
                <div className="text-sm font-mono-tech text-slate-700 mt-1" data-testid="tile-next-due">{insp?.next_due || "—"}</div>
                <div className="text-xs text-primary mt-3">Kalender öffnen →</div>
              </div>
            </div>
          </Card>
        </Link>

        <Link to="/pruefungen?scope=overdue" className="block" data-testid="tile-overdue-link">
          <Card className={`p-5 relative overflow-hidden fade-up cursor-pointer transition-shadow hover:shadow-md ${(insp?.overdue_total ?? 0) > 0 ? "border-red-300 bg-red-50/40" : "hover:border-primary/40"}`} data-testid="tile-overdue">
            <div className="kpi-accent-bar absolute top-0 left-0 right-0" style={{ background: "#DC2626" }} />
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 text-slate-500 text-sm font-semibold"><AlarmClock className="h-4 w-4 text-red-600" />Überfällige Prüfungen</div>
                <div className={`text-4xl font-head font-extrabold font-mono-tech mt-2 ${(insp?.overdue_total ?? 0) > 0 ? "text-red-600" : "text-slate-900"}`} data-testid="tile-overdue-count">{insp?.overdue_total ?? 0}</div>
                <div className="text-xs text-slate-500">überfällige Vorgänge</div>
                <div className="flex gap-4 mt-3 text-sm">
                  <span className="text-red-600 font-semibold">{insp?.overdue_inspections ?? 0}<span className="text-slate-400 font-normal"> Prüfungen</span></span>
                  <span className="text-orange-600 font-semibold">{insp?.overdue_maintenance ?? 0}<span className="text-slate-400 font-normal"> Wartungen</span></span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400">Längste Überschreitung</div>
                <div className="text-sm font-mono-tech text-red-600 font-bold mt-1">{insp?.longest_overdue_days ? `${insp.longest_overdue_days} Tage` : "—"}</div>
                <div className="text-xs text-primary mt-3">Liste öffnen →</div>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><FolderKanban className="h-5 w-5 text-primary" />Projektübersicht</h3>
          <div className="space-y-3">
            {data.projects.length === 0 && <p className="text-slate-400 text-sm">Noch keine Projekte angelegt.</p>}
            {data.projects.map((p) => (
              <Link to={`/projects/${p.id}`} key={p.id} data-testid={`dash-project-${p.id}`}
                className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:border-primary/40 hover:bg-slate-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.open_tickets} offene Tickets</div>
                </div>
                <div className="w-40 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Progress value={p.progress} className="h-1.5 flex-1" />
                    <span className="text-xs font-mono-tech text-slate-600 w-9 text-right">{p.progress}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={p.total_defects ? Math.round((p.open_defects || 0) / p.total_defects * 100) : 0} className="h-1.5 flex-1 [&>div]:bg-red-500" />
                    <span className="text-xs font-mono-tech text-red-600 w-9 text-right flex items-center justify-end gap-0.5" data-testid={`dash-open-defects-${p.id}`}><AlertTriangle className="h-2.5 w-2.5" />{p.open_defects || 0}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><AlarmClock className="h-5 w-5 text-red-500" />Überfällig & Heute</h3>
          <div className="space-y-2.5 max-h-80 overflow-auto scroll-thin">
            {[...data.overdue_list, ...data.today_deadlines].length === 0 && <p className="text-slate-400 text-sm">Keine anstehenden Fristen.</p>}
            {data.overdue_list.map((t) => (
              <div key={t.id} className="flex items-center gap-3 text-sm" data-testid={`overdue-${t.id}`}>
                <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                <div className="flex-1 min-w-0"><div className="truncate text-slate-800">{t.title}</div><div className="text-xs text-slate-400 truncate">{t.project_name}</div></div>
                <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50 text-[10px]">{t.due_date}</Badge>
              </div>
            ))}
            {data.today_deadlines.map((t) => (
              <div key={t.id} className="flex items-center gap-3 text-sm">
                <span className="h-2 w-2 rounded-full bg-sky-500 shrink-0" />
                <div className="flex-1 min-w-0"><div className="truncate text-slate-800">{t.title}</div><div className="text-xs text-slate-400 truncate">{t.project_name}</div></div>
                <Badge variant="outline" className="text-sky-600 border-sky-200 bg-sky-50 text-[10px]">Heute</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-stretch">
        <Card className="p-5 h-full" data-testid="dash-recent-photos">
          <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><ImageIcon className="h-5 w-5 text-primary" />Zuletzt erfasste Fotos</h3>
          {data.recent_photos.length === 0 ? <p className="text-slate-400 text-sm">Noch keine Fotos hochgeladen.</p> : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {data.recent_photos.map((f) => (
                <button key={f.id} onClick={() => setLightbox(f)} className="cursor-pointer transition-opacity hover:opacity-80 min-w-0" data-testid={`recent-photo-${f.id}`}>
                  <AuthImage fileId={f.id} className="aspect-square w-full rounded-lg object-cover border border-slate-200" />
                </button>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5 h-full" data-testid="dash-recent-plans">
          <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><FileStack className="h-5 w-5 text-emerald-600" />Zuletzt bearbeitete Baupläne</h3>
          {data.recent_plans.length === 0 ? <p className="text-slate-400 text-sm">Noch keine Baupläne hochgeladen.</p> : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {data.recent_plans.map((pl) => (
                <Link to={`/plans/${pl.id}`} key={pl.id} data-testid={`recent-plan-${pl.id}`}
                  className="aspect-square min-w-0 flex flex-col items-center justify-center gap-1 p-1.5 rounded-lg border border-slate-200 bg-emerald-50/40 hover:border-primary/40 hover:bg-slate-50 transition-colors text-center">
                  <FileStack className="h-6 w-6 text-emerald-600 shrink-0" />
                  <div className="w-full truncate font-medium text-slate-800 text-xs">{pl.name}</div>
                  <div className="w-full truncate text-[10px] text-slate-400">{pl.project_name}</div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
      {lightbox && <Lightbox fileId={lightbox.id} caption={lightbox.description || lightbox.original_filename} onClose={() => setLightbox(null)} />}
    </div>
  );
}
